import {
  forwardRef,
  HTMLAttributes,
  HTMLInputTypeAttribute,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  Block,
  Input as StyledInput,
  InputAfter,
  Label,
  Wrapper,
  MeasureContent,
} from './_styles'

export interface InputProps extends HTMLAttributes<HTMLInputElement> {
  value?: string
  hasError?: boolean
  label?: string
  labelTestId?: string
  before?: ReactNode
  beforeText?: ReactNode
  after?: ReactNode
  afterText?: ReactNode
  readOnly?: boolean
  disabled?: boolean
  autoFocus?: boolean
  name?: string
  placeholder?: string
  size?: 'M' | 'S'
  maxLength?: number
  type?: HTMLInputTypeAttribute
  pattern?: string
  autoWidth?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    hasError, // add red border
    label,
    labelTestId,
    before, // slot, displays content at the start of input
    beforeText, // slot, displays content at the start of native input
    after, // slot, displays content at the end of input
    afterText, // slot, displays content at the end of native input
    size,
    autoWidth, // if true, the input will be displayed as a block with a width of the content
    ...inputProps // other props passed to native input
  } = props

  const [dynamicWidth, setDynamicWidth] = useState(0)
  const measureContentRef = useRef<HTMLSpanElement>(null)

  useLayoutEffect(() => {
    if (autoWidth && measureContentRef.current) {
      setDynamicWidth(measureContentRef.current.offsetWidth)
    }
  }, [inputProps.value, label, inputProps.placeholder, autoWidth])

  const measureContent =
    inputProps.value || label || inputProps.placeholder || ''

  return (
    <Wrapper
      readOnly={inputProps.readOnly}
      disabled={inputProps.disabled}
      $size={size}
      style={autoWidth ? { width: 'auto', display: 'inline-flex' } : {}}
    >
      {before}
      <Block readOnly={inputProps.readOnly} disabled={inputProps.disabled}>
        {beforeText}
        <StyledInput
          spellCheck='false'
          autoComplete='off'
          placeholder=' '
          {...inputProps}
          style={
            autoWidth
              ? { width: dynamicWidth ? `${dynamicWidth}px` : 'auto' }
              : inputProps?.style ?? {}
          }
          hasError={hasError}
          hasLabel={Boolean(label)}
          ref={ref}
          $size={size}
        />
        {afterText}

        <Label
          htmlFor={inputProps.id}
          hasError={hasError}
          data-testid={labelTestId}
        >
          {label}
        </Label>
        {autoWidth && (
          <MeasureContent
            ref={measureContentRef}
            $size={size}
            $useInputFontStyle={!inputProps.value && Boolean(label)}
          >
            {measureContent}
          </MeasureContent>
        )}
      </Block>
      {after && <InputAfter $size={size}>{after}</InputAfter>}
    </Wrapper>
  )
})

export default Input
